import React, { useEffect, useRef, useCallback, useState } from "react";
import { push } from "connected-react-router";
import { reducerType } from "redux/reducers";
import { connect, useSelector } from "react-redux";
import { Dispatch } from "redux";
import HeaderBarSub from "components/HeaderBarSub";
import Container from "@material-ui/core/Container";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import SwapVertIcon from "@material-ui/icons/SwapVert";
import styles from "./styles";
import GridFullHeight from "components/GridFullHeight";
import GridFormContainer from "components/GridFormContainer";
import {
  GET_VALIDATE_SERVICES_REQUEST,
  CLEAR_VALIDATE_SERVICES,
  LOAD_MORE_VALIDATE_SERVICES_REQUEST,
} from "redux/reducers/service/actionTypes";
import Loading from "components/Loading";
import MSService from "containers/MSServiceContainer";
import { ServiceProps } from "common/";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

const sortOptions = [
  { value: "updatedAt", labelKey: "LEAST_RECENTLY_UPDATED_SERVICE" },
  { value: "nearestToMe", labelKey: "SORT_NEAREST" },
];

const defaultSortValue = sortOptions[0].value;

interface ValidateServicesProps {
  dispatch: Dispatch;
  loading: boolean;
  services: ServiceProps[];
  loadingMore: boolean;
  canLoadMore: boolean;
}

const mapStateToProps = (state: reducerType) => {
  return {
    loading: state.service.loading,
    services: state.service.validateServices,
    loadingMore: state.service.loadingMore,
    canLoadMore: state.service.canLoadmore,
  };
};

const ValidateListServices = React.memo((props: ValidateServicesProps) => {
  const { dispatch, loading, services, loadingMore, canLoadMore } = props;
  const classes = styles();
  const translate = useTranslation().t;
  const isLoadingMoreRef = useRef(false);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [sortOption, setSortOption] = useState<string>(defaultSortValue);
  const current_user = useSelector(
    (state: reducerType) => state.auth.current_user
  );
  const openUrl = (url: string) => {
    dispatch(push(url));
  };

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (sort: string) => {
    setSortOption(sort);
    handleSortClose();
    const params: Record<string, string | boolean> = { sort };
    dispatch({
      type: GET_VALIDATE_SERVICES_REQUEST,
      params,
    });
  };

  const getSortLabel = () => {
    const option = sortOptions.find((opt) => opt.value === sortOption);
    return option ? translate(option.labelKey) : translate("SORT");
  };

  const loadMoreServices = useCallback(() => {
    dispatch({
      type: LOAD_MORE_VALIDATE_SERVICES_REQUEST,
    });
  }, [dispatch]);

  const handleWindowScroll = useCallback(() => {
    if (loading || loadingMore || isLoadingMoreRef.current) return;

    const bottomOfWindow =
      window.scrollY + window.innerHeight >= document.body.scrollHeight - 500;
    if (bottomOfWindow && canLoadMore) {
      isLoadingMoreRef.current = true;
      loadMoreServices();
    }
  }, [loading, loadingMore, canLoadMore, loadMoreServices]);

  useEffect(() => {
    window.addEventListener("scroll", handleWindowScroll);
    return () => {
      window.removeEventListener("scroll", handleWindowScroll);
    };
  }, [handleWindowScroll]);

  useEffect(() => {
    if (!loadingMore) {
      isLoadingMoreRef.current = false;
    }
  }, [loadingMore]);

  useEffect(() => {
    const search = typeof window !== "undefined" ? window.location.search : "";
    const searchParams = new URLSearchParams(search);
    const sortFromUrl = searchParams.get("sort") || defaultSortValue;
    const sort = sortOptions.some((o) => o.value === sortFromUrl) ? sortFromUrl : defaultSortValue;
    const filter = searchParams.get("filter") || "";
    setSortOption(sort);
    const params: Record<string, string | boolean> = { sort };
    if (filter) {
      params.filter = filter;
      params[filter] = true;
    }
    dispatch({
      type: GET_VALIDATE_SERVICES_REQUEST,
      params,
    });

    // Clear state when component unmounts (when leaving the page)
    return () => {
      dispatch({
        type: CLEAR_VALIDATE_SERVICES,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GridFullHeight container>
      <GridFormContainer item xs={12} sm={12} md={12}>
        <GridFullHeight container>
          <HeaderBarSub
            backUrl={"/"}
            openUrl={openUrl}
            name={translate("VALIDATE_SERVICES")}
            isSearch
          />
          {/* Sort Tools */}
          <Container className={classes.toolsContainer}>
            <Button
              className={clsx(classes.sortButton, sortOption)}
              onClick={handleSortClick}
              startIcon={<SwapVertIcon />}
            >
              {getSortLabel()}
            </Button>
            <Menu anchorEl={sortAnchorEl} keepMounted open={Boolean(sortAnchorEl)} onClose={handleSortClose}>
              {sortOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  className={classes.filterMenuItem}
                  selected={sortOption === option.value}
                  onClick={() => handleSortSelect(option.value)}
                >
                  {translate(option.labelKey)}
                </MenuItem>
              ))}
            </Menu>
          </Container>
          <Container className={classes.root}>
            {loading ? (
              <Loading />
            ) : (
              <>
                {services && services.length > 0 ? (
                  services.map((data, index) => (
                    <MSService
                      data={data}
                      key={index}
                      openUrl={openUrl}
                      fromValidateListServicesPage={true}
                      userRole={current_user.role}
                    />
                  ))
                ) : (
                  <p className={classes.noText}>
                    {translate("NO_SERVICES_TO_VALIDATE")}
                  </p>
                )}
                {canLoadMore && (
                  <Container className={classes.textCenter}>
                    <CircularProgress
                      style={{ width: 25, height: 25 }}
                      className={classes.loadmore}
                    />
                  </Container>
                )}
              </>
            )}
          </Container>
        </GridFullHeight>
      </GridFormContainer>
    </GridFullHeight>
  );
});

export default connect(mapStateToProps)(ValidateListServices);
